/**
 * Calendar Zustand store — external iCal calendars.
 *
 * 1. Load the device-local cache (`#ical-calendars-data` in `setting_local`) once per session.
 * 2. Fetch feeds through the API proxy (`POST /ical/proxy`), parse client-side (icalParser.ts),
 *    keep per-calendar status, never replace good data with a failed fetch (calendarSync.ts).
 * 3. Expand occurrences for the default window (30 days back, a year ahead), memoised per
 *    calendar data + window + viewer zone, so mounting a view never re-expands.
 * 4. Follow config changes (add / edit / delete) without waiting for the refresh interval.
 *
 * Feed URLs are secrets (anyone with one can read the calendar): they are never logged and never
 * sent in a query string.
 */

import { create } from "zustand"
import { db } from "@/db/schema"
import { apiRequest } from "@/shared/utils/api/apiClient"
import {
  applyFetchOutcome,
  errorCodeForStatus,
  mapWithConcurrency,
  outcomeFromText,
  readCalendarCache,
  statusFromFeed,
  writeCalendarCache,
  hashText,
  type CachedCalendar,
  type FetchOutcome,
} from "./calendarSync"
import { expandCalendarEventsAsync, expansionWindow, convertWebcalToHttps } from "./icalUtils"
import type { ParsedFeed } from "./icalParser"
import { localDayKey, viewerZone } from "./zonedTime"
import type { CalendarStatus, EventOccurrence, ICalCalendar, ICalEventItem } from "./types"
import { normalizeCalendarConfigs } from "./types"

const CACHE_ID = "#ical-calendars-data"
const CONFIG_ID = "#ical-calendars"
/** Calendars fetched at most (each can take up to the proxy's 15 s). */
const MAX_CALENDARS = 20
/** Parallel proxy requests in a full refresh (the proxy allows 40/min per user). */
const FETCH_CONCURRENCY = 3
/** Above the proxy's own 15 s upstream budget. */
const FETCH_TIMEOUT_MS = 25_000

// ---------------------------------------------------------------------------
// localStorage immediate cache — sync read on store init to avoid layout jumps
// ---------------------------------------------------------------------------

const LS_CALENDAR_KEY = "totodo:calendar-events:v2"
const LS_LEGACY_KEYS = ["totodo:calendar-events"]

/** Max events to cache in localStorage (keep it compact) */
const MAX_CACHED_EVENTS = 200

/** Fields to keep per EventOccurrence for the localStorage cache */
const CACHED_EVENT_FIELDS: (keyof EventOccurrence)[] = [
  "uid",
  "summary",
  "occurrenceDate",
  "occurrenceStart",
  "occurrenceEnd",
  "color",
  "allDay",
  "rrule",
  "isFloating",
  "multiDay",
  "occurrences",
  "calendarId",
]

function slimEvent(event: EventOccurrence): Partial<EventOccurrence> {
  const slim: Record<string, unknown> = {}
  for (const key of CACHED_EVENT_FIELDS) {
    const val = event[key]
    if (val !== undefined && val !== null) slim[key] = val
  }
  return slim as Partial<EventOccurrence>
}

/** Write the nearest events to localStorage for instant hydration on next visit. */
function cacheEventsToLocalStorage(events: EventOccurrence[]): void {
  try {
    const todayStr = localDayKey(Date.now())
    const sorted = [...events].sort((a, b) => {
      const aDate = a.occurrenceDate
      const bDate = b.occurrenceDate
      const aFuture = aDate >= todayStr
      const bFuture = bDate >= todayStr
      if (aFuture && bFuture) return aDate < bDate ? -1 : aDate > bDate ? 1 : 0
      if (aFuture && !bFuture) return -1
      if (!aFuture && bFuture) return 1
      return bDate < aDate ? -1 : bDate > aDate ? 1 : 0
    })
    localStorage.setItem(
      LS_CALENDAR_KEY,
      JSON.stringify({
        events: sorted.slice(0, MAX_CACHED_EVENTS).map(slimEvent),
        cachedAt: Date.now(),
      }),
    )
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

function readCachedEventsFromLocalStorage(): EventOccurrence[] {
  try {
    for (const key of LS_LEGACY_KEYS) localStorage.removeItem(key)
    const raw = localStorage.getItem(LS_CALENDAR_KEY)
    if (!raw) return []
    const data = JSON.parse(raw) as { events: Partial<EventOccurrence>[]; cachedAt: number }
    // Expire after 24 hours — data will be refreshed from Dexie/API anyway
    if (Date.now() - data.cachedAt > 24 * 60 * 60 * 1000) return []
    return (data.events ?? []) as EventOccurrence[]
  } catch {
    return []
  }
}

const initialEvents = readCachedEventsFromLocalStorage()

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

/** A calendar as the UI sees it: config (name, color) + data + status. */
export interface CalendarRuntime extends CachedCalendar {
  name: string
  color: string
}

interface CalendarState {
  calendars: CalendarRuntime[]
  allEvents: EventOccurrence[]
  loading: boolean
  /** Calendar ids with a fetch in flight. */
  fetching: string[]
}

export interface FetchOptions {
  /** User-initiated: ask the proxy to bypass its 5-minute cache. */
  refresh?: boolean
  /** Only these calendars (default: all configured). */
  ids?: string[]
}

interface CalendarActions {
  /** Load the device-local cache. Idempotent: only the first call per session reads Dexie. */
  loadCachedCalendars: () => Promise<void>
  /** Fetch, parse and cache the configured calendars. Concurrent calls per calendar share one fetch. */
  fetchAndUpdateCalendars: (options?: FetchOptions) => Promise<void>
  /**
   * Reconcile with the configs: drop deleted calendars, apply name/colour changes, fetch new ones
   * and ones whose URL changed.
   */
  applyConfigs: (configs: ICalCalendar[]) => Promise<void>
  /** Take a feed the settings page already fetched and parsed (no second request). */
  ingestFeed: (config: ICalCalendar, text: string, feed: ParsedFeed) => Promise<void>
  /** Re-expand if the day changed since the last expansion (cheap otherwise). */
  ensureCurrentWindow: () => Promise<void>
  /** Clear all calendar data. */
  clearCalendars: () => void
}

// ---------------------------------------------------------------------------
// Expansion memo (per calendar data array + window + viewer zone)
// ---------------------------------------------------------------------------

interface MemoEntry {
  key: string
  occurrences: EventOccurrence[]
}
const expansionMemo = new WeakMap<ICalEventItem[], MemoEntry>()
let lastWindowKey = ""

function windowKey(): string {
  const w = expansionWindow()
  return `${w.startDay}|${w.endDay}|${viewerZone()}`
}

/** Yield to the event loop so a large expansion does not block input. */
const yieldToMain = () => new Promise<void>((resolve) => setTimeout(resolve, 0))

let recomputeGeneration = 0

/** Combine every calendar's occurrences (expanding only what the memo lacks). */
async function computeAllEvents(calendars: CalendarRuntime[]): Promise<EventOccurrence[] | null> {
  const generation = ++recomputeGeneration
  const key = windowKey()
  lastWindowKey = key
  const out: EventOccurrence[] = []
  for (const cal of calendars) {
    let memo = expansionMemo.get(cal.events)
    if (!memo || memo.key !== key) {
      await yieldToMain()
      if (generation !== recomputeGeneration) return null
      memo = { key, occurrences: await expandCalendarEventsAsync(cal.events, cal.id) }
      if (generation !== recomputeGeneration) return null
      expansionMemo.set(cal.events, memo)
    }
    for (const occ of memo.occurrences) {
      occ.color = cal.color
      out.push(occ)
    }
  }
  return generation === recomputeGeneration ? out : null
}

// ---------------------------------------------------------------------------
// Dexie helpers
// ---------------------------------------------------------------------------

async function readConfigs(): Promise<ICalCalendar[]> {
  const entry = await db.setting.get(CONFIG_ID)
  if (!entry?.value) return []
  let raw: unknown = entry.value
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw)
    } catch {
      return []
    }
  }
  return normalizeCalendarConfigs(raw).filter((c) => c.url)
}

async function persistCache(calendars: CalendarRuntime[]): Promise<void> {
  try {
    await db.setting_local.put({
      id: CACHE_ID,
      value: writeCalendarCache(calendars),
      device: "n/a",
      createdAt: String(Date.now()),
      createdBy: "system",
    })
  } catch {
    console.warn("[calendar] could not write the calendar cache")
  }
}

// ---------------------------------------------------------------------------
// Network
// ---------------------------------------------------------------------------

/** Fetch one feed through the proxy. Never throws; never logs the URL. */
export async function fetchFeedText(
  url: string,
  refresh = false,
): Promise<{ ok: true; text: string } | { ok: false; outcome: FetchOutcome }> {
  try {
    const response = await apiRequest<unknown>({
      path: "/ical/proxy",
      method: "POST",
      body: refresh ? { url: convertWebcalToHttps(url), refresh: true } : { url: convertWebcalToHttps(url) },
      timeout: FETCH_TIMEOUT_MS,
    })
    if (!response.ok) {
      return {
        ok: false,
        outcome: { kind: "error", code: errorCodeForStatus(response.status), status: response.status },
      }
    }
    if (typeof response.data !== "string") {
      return { ok: false, outcome: { kind: "error", code: "not-ical" } }
    }
    return { ok: true, text: response.data }
  } catch {
    return { ok: false, outcome: { kind: "error", code: "network" } }
  }
}

function withConfig(cal: CachedCalendar, config: ICalCalendar): CalendarRuntime {
  return { ...cal, id: config.id, name: config.name, color: config.color }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

let cacheLoad: Promise<void> | null = null
const inflight = new Map<string, Promise<void>>()

export const useCalendarStore = create<CalendarState & CalendarActions>()(
  (set, get) => {
    /** Publish calendars and their combined occurrences (expansion memoised, off the render path). */
    const publish = async (calendars: CalendarRuntime[], persist: boolean) => {
      set({ calendars })
      const allEvents = await computeAllEvents(calendars)
      if (allEvents) {
        set({ allEvents, loading: false })
        cacheEventsToLocalStorage(allEvents)
      }
      if (persist) await persistCache(calendars)
    }

    /** Fetch one calendar and merge the result into the current state. */
    const fetchOne = (config: ICalCalendar, refresh: boolean): Promise<void> => {
      const running = inflight.get(config.id)
      if (running) return running
      const job = (async () => {
        set({ fetching: [...get().fetching, config.id] })
        try {
          const result = await fetchFeedText(config.url, refresh)
          const prev = get().calendars.find((c) => c.id === config.id)
          const outcome = result.ok ? outcomeFromText(result.text, prev) : result.outcome
          const next = applyFetchOutcome(prev, { id: config.id, url: config.url }, outcome, Date.now())
          if (outcome.kind === "error") {
            console.warn(`[calendar] refresh failed for a calendar: ${outcome.code}${outcome.status ? ` (${outcome.status})` : ""}`)
          }
          // The config may have been deleted or changed while fetching
          const current = await readConfigs()
          const stillConfigured = current.find((c) => c.id === config.id && c.url === config.url)
          if (!stillConfigured) return
          const calendars = get().calendars.filter((c) => c.id !== config.id)
          const merged = withConfig(next, stillConfigured)
          const ordered = current
            .map((c) => (c.id === merged.id ? merged : calendars.find((x) => x.id === c.id)))
            .filter((c): c is CalendarRuntime => !!c)
          await publish(ordered, true)
        } finally {
          inflight.delete(config.id)
          set({ fetching: get().fetching.filter((id) => id !== config.id) })
        }
      })()
      inflight.set(config.id, job)
      return job
    }

    return {
      calendars: [],
      allEvents: initialEvents,
      loading: initialEvents.length === 0,
      fetching: [],

      loadCachedCalendars: () => {
        if (!cacheLoad) {
          cacheLoad = (async () => {
            try {
              // Only the device-local table. A `#ical-calendars-data` row in the synced `setting`
              // table may belong to v1 clients: it is neither read nor deleted
              // (STARTUP_LOGIN_SYNC_AUDIT SYNC-06 / BG-05).
              const entry = await db.setting_local.get(CACHE_ID)
              const cached = readCalendarCache(entry?.value)
              const configs = await readConfigs()
              const calendars = configs
                .map((config) => {
                  const hit = cached.find((c) => c.id === config.id && c.url === config.url)
                  return hit ? withConfig(hit, config) : null
                })
                .filter((c): c is CalendarRuntime => !!c)
              if (get().calendars.length === 0 && calendars.length > 0) {
                await publish(calendars, false)
              } else if (configs.length === 0) {
                set({ calendars: [], allEvents: [], loading: false })
              } else {
                set({ loading: false })
              }
            } catch {
              console.warn("[calendar] could not read the calendar cache")
              set({ loading: false })
            }
          })()
        }
        return cacheLoad.then(() => get().ensureCurrentWindow())
      },

      fetchAndUpdateCalendars: async (options = {}) => {
        const configs = (await readConfigs()).slice(0, MAX_CALENDARS)
        if (configs.length === 0) {
          set({ calendars: [], allEvents: [], loading: false })
          cacheEventsToLocalStorage([])
          return
        }
        const targets = options.ids ? configs.filter((c) => options.ids!.includes(c.id)) : configs
        await mapWithConcurrency(targets, FETCH_CONCURRENCY, (c) => fetchOne(c, !!options.refresh))
        if (get().loading) set({ loading: false })
      },

      applyConfigs: async (rawConfigs) => {
        const configs = normalizeCalendarConfigs(rawConfigs)
          .filter((c) => c.url)
          .slice(0, MAX_CALENDARS)
        if (cacheLoad) await cacheLoad
        const current = get().calendars
        const next: CalendarRuntime[] = []
        const toFetch: ICalCalendar[] = []
        for (const config of configs) {
          const existing = current.find((c) => c.id === config.id)
          if (existing && existing.url === config.url) {
            next.push(withConfig(existing, config))
          } else {
            toFetch.push(config)
          }
        }
        const changed =
          next.length !== current.length ||
          next.some((c, i) => {
            const old = current[i]
            return !old || old.id !== c.id || old.color !== c.color || old.name !== c.name
          })
        if (changed) await publish(next, true)
        if (configs.length === 0) cacheEventsToLocalStorage([])
        await mapWithConcurrency(toFetch, FETCH_CONCURRENCY, (c) => fetchOne(c, false))
      },

      ingestFeed: async (config, text, feed) => {
        if (cacheLoad) await cacheLoad
        const now = Date.now()
        const cal: CalendarRuntime = {
          id: config.id,
          url: config.url,
          name: config.name,
          color: config.color,
          contentHash: hashText(text),
          calendarName: feed.calendarName,
          events: feed.events,
          status: statusFromFeed(feed, now),
        }
        const others = get().calendars.filter((c) => c.id !== config.id)
        await publish([...others, cal], true)
      },

      ensureCurrentWindow: async () => {
        if (lastWindowKey === "" || lastWindowKey === windowKey()) return
        await publish(get().calendars, false)
      },

      clearCalendars: () => {
        set({ calendars: [], allEvents: [] })
        try {
          localStorage.removeItem(LS_CALENDAR_KEY)
        } catch {
          // ignore
        }
      },
    }
  },
)

/** Status of one calendar (settings rows). */
export function useCalendarStatus(id: string): {
  status: CalendarStatus | undefined
  calendarName: string | null | undefined
  fetching: boolean
} {
  const cal = useCalendarStore((s) => s.calendars.find((c) => c.id === id))
  const fetching = useCalendarStore((s) => s.fetching.includes(id))
  return { status: cal?.status, calendarName: cal?.calendarName, fetching }
}
